import { useAuthStore } from '@sentinel/web-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MissionSummary } from '@sentinel/shared';
import { endpoints } from '../api/endpoints';
import { keys } from '@/core/query/query-keys';

/** The open missions of the organization and the one the user is working on.
 *  The choice is saved in the profile so it follows the user across devices. */
export function useOpenMissions() {
  return useQuery({
    queryKey: keys.missions('open'),
    queryFn: () => endpoints.missions.list({ status: 'open', pageSize: 50 }),
  });
}

export function useActiveMission(): {
  mission: MissionSummary | null;
  missionId: string | null;
  isLoading: boolean;
} {
  const activeMissionId = useAuthStore((s) => s.me?.activeMissionId ?? null);
  const missions = useOpenMissions();
  const items = missions.data?.items ?? [];
  const mission = items.find((m) => m.id === activeMissionId) ?? items[0] ?? null;
  return { mission, missionId: mission?.id ?? null, isLoading: missions.isLoading };
}

export function useSelectMission() {
  const queryClient = useQueryClient();
  const setMe = useAuthStore((s) => s.setMe);
  return useMutation({
    mutationFn: (missionId: string) => endpoints.auth.preferences({ activeMissionId: missionId }),
    onSuccess: (me) => {
      setMe(me);
      void queryClient.invalidateQueries({ queryKey: ['mission'] });
    },
  });
}
